import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import SaveIcon from "@mui/icons-material/Save";
import "./PaymentMethodSetup.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Available payment methods
const paymentMethods = [
  "Advance Payment",
  "Letter of Credit",
  "Open Account",
  "Documentary Collection",
  "Bank Contract",
  "Consignment Basis",
];

// Default document flow for each payment method
const defaultDocumentFlow = {
  "Advance Payment": [
    "Proforma Invoice",
    "Bank remittance receipt",
    "Purchase Order",
    "Commercial Invoice",
    "Packing List",
    "Bill of Lading",
    "Goods Declaration",
    "Import General Manifest",
  ],
  "Letter of Credit": [
    "Proforma Invoice",
    "L/C Application",
    "L/C Issuance",
    "Purchase Order",
    "Commercial Invoice",
    "Packing List",
    "Certificate of Origin",
    "Inspection Certificate",
    "Bill of Lading",
    "Bill of Exchange",
    "Goods Declaration",
    "L/C Settlement Document",
  ],
  "Open Account": [
    "Proforma Invoice",
    "Purchase Order",
    "Commercial Invoice",
    "Packing List",
    "Bill of Lading",
    "Delivery Order",
    "Goods Declaration",
  ],
  "Documentary Collection": [
    "Proforma Invoice",
    "Purchase Order",
    "Commercial Invoice",
    "Packing List",
    "Bill of Lading",
    "Goods Declaration",
  ],
  "Bank Contract": [
    "Proforma Invoice",
    "FE Contract",
    "Purchase Order",
    "Shipment",
    "Commercial Invoice",
    "Packing List",
    "Bill of Lading",
    "Goods Declaration",
    "Bank Payment Approval",
    "Import contract form",
  ],
  "Consignment Basis": [
    "Proforma Invoice",
    "Consignment Agreement",
    "Packing List",
    "Commercial Invoice",
    "Bill of Lading",
    "Goods Declaration",
    "Sales Report",
  ],
};

const PaymentMethodSetup = () => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [savedConfigurations, setSavedConfigurations] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Load saved configurations from localStorage on mount
  useEffect(() => {
    loadConfigurations();
  }, []);

  // Update available and selected documents when payment method changes
  useEffect(() => {
    if (selectedPaymentMethod) {
      const defaultDocs = defaultDocumentFlow[selectedPaymentMethod] || [];
      setAvailableDocuments(defaultDocs);

      const saved = savedConfigurations[selectedPaymentMethod];
      if (saved && saved.length > 0) {
        setSelectedDocuments(saved);
      } else {
        setSelectedDocuments(defaultDocs);
      }
      setHasChanges(false);
    } else {
      setAvailableDocuments([]);
      setSelectedDocuments([]);
    }
  }, [selectedPaymentMethod, savedConfigurations]);

  const loadConfigurations = () => {
    try {
      const stored = localStorage.getItem("paymentMethodDocuments");
      if (stored) {
        const parsed = JSON.parse(stored);
        setSavedConfigurations(parsed);
      } else {
        localStorage.setItem("paymentMethodDocuments", JSON.stringify(defaultDocumentFlow));
        setSavedConfigurations(defaultDocumentFlow);
      }
    } catch (error) {
      console.error("Error loading configurations:", error);
      toast.error("Failed to load saved configurations");
    } finally {
      setInitialLoading(false);
    }
  };

  const handlePaymentMethodChange = (event) => {
    if (hasChanges) {
      const confirmSwitch = window.confirm(
        "You have unsaved changes. Do you want to discard them and switch payment method?"
      );
      if (!confirmSwitch) return;
    }
    setSelectedPaymentMethod(event.target.value);
  };

  const handleSummaryCardClick = (method) => {
    if (hasChanges) {
      const confirmSwitch = window.confirm(
        "You have unsaved changes. Do you want to discard them and switch payment method?"
      );
      if (!confirmSwitch) return;
    }
    setSelectedPaymentMethod(method);
  };

  const handleDocumentToggle = (document) => {
    setSelectedDocuments((prev) => {
      const newSelection = prev.includes(document)
        ? prev.filter((doc) => doc !== document)
        : [...prev, document];
      setHasChanges(true);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    setSelectedDocuments(availableDocuments);
    setHasChanges(true);
  };

  const handleDeselectAll = () => {
    setSelectedDocuments([]);
    setHasChanges(true);
  };

  const handleResetToDefault = () => {
    if (!selectedPaymentMethod) return;
    
    const confirmReset = window.confirm(
      "Are you sure you want to reset to default documents for this payment method?"
    );
    
    if (confirmReset) {
      const defaultDocs = defaultDocumentFlow[selectedPaymentMethod] || [];
      setSelectedDocuments(defaultDocs);
      setHasChanges(true);
      toast.info("Reset to default configuration");
    }
  };

  const handleSave = () => {
    if (!selectedPaymentMethod) {
      toast.warn("Please select a payment method");
      return;
    }

    if (selectedDocuments.length === 0) {
      toast.warn("Please select at least one document");
      return;
    }

    setLoading(true);

    try {
      const updatedConfigurations = {
        ...savedConfigurations,
        [selectedPaymentMethod]: selectedDocuments,
      };

      localStorage.setItem(
        "paymentMethodDocuments",
        JSON.stringify(updatedConfigurations)
      );

      setSavedConfigurations(updatedConfigurations);
      setHasChanges(false);

      toast.success(
        `Configuration saved for ${selectedPaymentMethod}`,
        {
          position: "bottom-right",
          autoClose: 2000,
        }
      );
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast.error("Failed to save configuration");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box className="payment-setup-full-container">
        <Box className="payment-setup-loading">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="payment-setup-full-container">
      <Box className="payment-setup-header">
        <Typography variant="h4" className="payment-setup-title">
          Payment Method Document Setup
        </Typography>
        <Typography variant="body1" className="payment-setup-subtitle">
          Configure which documents are required for each payment method
        </Typography>
      </Box>

      <Card className="payment-setup-card">
        <CardContent>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Payment Method</InputLabel>
            <Select
              value={selectedPaymentMethod}
              label="Select Payment Method"
              onChange={handlePaymentMethodChange}
            >
              {paymentMethods.map((method) => {
                const defaultCount = defaultDocumentFlow[method]?.length || 0;
                const savedCount = savedConfigurations[method]?.length || 0;
                const isCustomized = savedCount > 0 && savedCount !== defaultCount;
                
                return (
                  <MenuItem key={method} value={method}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <span>{method}</span>
                      <Chip
                        label={`${savedCount || defaultCount} docs`}
                        size="small"
                        color={isCustomized ? "success" : "primary"}
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedPaymentMethod && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Showing documents for <strong>{selectedPaymentMethod}</strong>. 
                Select the documents you want to require for this payment method.
              </Alert>

              <Box className="payment-setup-actions">
                <Typography variant="body2" className="document-count">
                  Selected: {selectedDocuments.length} / {availableDocuments.length}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {availableDocuments.length > 0 ? (
                <Box className="documents-grid">
                  {availableDocuments.map((document) => (
                    <FormControlLabel
                      key={document}
                      control={
                        <Checkbox
                          checked={selectedDocuments.includes(document)}
                          onChange={() => handleDocumentToggle(document)}
                          sx={{
                            color: "var(--primary-color)",
                            "&.Mui-checked": {
                              color: "var(--primary-color)",
                            },
                          }}
                        />
                      }
                      label={document}
                      className="document-checkbox"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" className="empty-state">
                  No documents available for this payment method
                </Typography>
              )}

              {hasChanges && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  You have unsaved changes
                </Alert>
              )}

              <Box className="payment-setup-footer">
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading || !hasChanges}
                  className="newt-button newt-button-primary"
                >
                  Save Configuration
                </Button>
              </Box>
            </>
          )}

          {!selectedPaymentMethod && (
            <Box className="empty-state">
              <Typography variant="body1" color="textSecondary">
                Select a payment method to configure its required documents
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card className="payment-setup-card summary-card">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration Summary
          </Typography>
          <Box className="summary-grid">
            {paymentMethods.map((method) => {
              const defaultCount = defaultDocumentFlow[method]?.length || 0;
              const savedCount = savedConfigurations[method]?.length || defaultCount;
              const isCustomized = savedConfigurations[method] && 
                                   savedConfigurations[method].length !== defaultCount;
              const isSelected = selectedPaymentMethod === method;
              
              return (
                <Box 
                  key={method} 
                  className={`summary-item ${isSelected ? 'summary-item-selected' : ''}`}
                  onClick={() => handleSummaryCardClick(method)}
                  sx={{ 
                    position: 'relative', 
                    padding: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: isSelected ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                    backgroundColor: isSelected ? 'rgba(var(--primary-color-rgb), 0.05)' : 'transparent',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      borderColor: 'var(--primary-color)',
                    }
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    className="summary-method"
                    sx={{ 
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      mb: 1,
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {method}
                  </Typography>
                  <Chip
                    label={`${savedCount} documents`}
                    color={isCustomized ? "success" : "primary"}
                    size="small"
                  />
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentMethodSetup;